import { appConfirm } from "@/lib/appDialog";
import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { EmptyState, ErrorState } from "@/components/StateViews";
import { toast } from "sonner";
import { Loader2, ClipboardList, FileSpreadsheet, FileText, Search, ArrowLeft, X, Download, Trash2 } from "lucide-react";

export default function SurveysAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get(`/admin/surveys${q ? `?q=${encodeURIComponent(q)}` : ""}`);
      setItems(data.surveys || []);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const openDetail = async (id) => {
    setSelectedId(id); setDetail(null);
    try {
      const { data } = await api.get(`/admin/surveys/${id}`);
      setDetail(data.survey);
    } catch (e) { toast.error(formatApiError(e)); setSelectedId(null); }
  };

  const downloadCsv = async () => {
    try {
      const res = await api.get("/admin/surveys/export/csv", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "emad-surveys.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const downloadAllPdf = async () => {
    try {
      const res = await api.get("/admin/surveys/export/pdf", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "emad-surveys.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const downloadOnePdf = async (id) => {
    try {
      const res = await api.get(`/admin/surveys/export/pdf/${id}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `survey-${id}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteSurvey = async (id, name) => {
    if (!await appConfirm(`حذف استبيان «${name || "الطالب"}»؟ سيتمكّن الطالب من إعادة تعبئته لاحقاً.`)) return;
    try {
      await api.delete(`/admin/surveys/${id}`);
      toast.success("تم حذف الاستبيان");
      if (selectedId === id) { setSelectedId(null); setDetail(null); }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <DashboardLayout>
      <div data-testid="admin-surveys">
        <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-teal-700">استبيانات الطلاب</div>
            <h1 className="mt-2 text-2xl font-heading font-extrabold text-slate-900">استبيانات الالتحاق برحلة عماد</h1>
            <p className="mt-2 text-slate-600 max-w-3xl">استعرض إجابات الطلاب فرداً فرداً، أو صدّرها Excel/PDF لتحليل بيانات مجموعة.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={downloadCsv} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-700 text-white text-sm font-semibold hover:bg-emerald-600" data-testid="export-csv">
              <FileSpreadsheet className="h-4 w-4" /> تصدير Excel (CSV)
            </button>
            <button onClick={downloadAllPdf} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-rose-700 text-white text-sm font-semibold hover:bg-rose-600" data-testid="export-all-pdf">
              <FileText className="h-4 w-4" /> تصدير الكل PDF
            </button>
          </div>
        </div>

        <div className="mb-4 relative max-w-md">
          <Search className="h-4 w-4 text-slate-400 absolute inset-y-0 start-3 my-auto" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث بالاسم أو البريد" className="w-full rounded-lg border border-slate-300 bg-white ps-9 pe-3 py-2.5 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none" data-testid="surveys-search" />
        </div>

        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
        ) : error ? (
          <ErrorState message={error} onRetry={load} testid="surveys-error" />
        ) : items.length === 0 ? (
          <EmptyState icon={ClipboardList} title="لا توجد استبيانات مُعبّأة بعد" description="ستظهر إجابات الطلاب هنا فور تعبئتهم للاستبيان الافتتاحي." testid="surveys-empty" />
        ) : (
          <div className="rounded-2xl bg-white border border-cream-200 card-shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-cream-50">
                  <tr>
                    <th className="text-start px-6 py-3 font-semibold text-slate-700">الاسم</th>
                    <th className="text-start px-6 py-3 font-semibold text-slate-700">البريد</th>
                    <th className="text-start px-6 py-3 font-semibold text-slate-700">المنطقة</th>
                    <th className="text-start px-6 py-3 font-semibold text-slate-700">الجامعة</th>
                    <th className="text-start px-6 py-3 font-semibold text-slate-700">التخصص الدقيق</th><th className="text-start px-6 py-3 font-semibold text-slate-700">المجال</th>
                    <th className="text-start px-6 py-3 font-semibold text-slate-700">النمط</th>
                    <th className="text-start px-6 py-3 font-semibold text-slate-700">التاريخ</th>
                    <th className="text-start px-6 py-3 font-semibold text-slate-700"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((s) => (
                    <tr key={s.id} className="border-t border-cream-100 hover:bg-cream-50/60" data-testid={`survey-row-${s.id}`}>
                      <td className="px-6 py-3 font-semibold text-slate-900">{s.student_name || "—"}</td>
                      <td className="px-6 py-3 text-slate-700" dir="ltr">{s.student_email || "—"}</td>
                      <td className="px-6 py-3 text-slate-700">{s.region || "—"}</td>
                      <td className="px-6 py-3 text-slate-700">{s.university || "—"}</td>
                      <td className="px-6 py-3 text-slate-700">{s.specialization || "—"}</td><td className="px-6 py-3 text-slate-700">{s.academic_field || "—"}</td>
                      <td className="px-6 py-3 text-slate-700">{s.preferred_mode || "—"}</td>
                      <td className="px-6 py-3 text-slate-500 text-xs arabic-numerals">{s.submitted_at?.slice(0, 10)}</td>
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-1.5">
                          <button onClick={() => openDetail(s.id)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-teal-50 text-teal-700 text-xs font-semibold hover:bg-teal-100" data-testid={`view-survey-${s.id}`}>
                            عرض <ArrowLeft className="h-3 w-3" />
                          </button>
                          <button onClick={() => downloadOnePdf(s.id)} className="inline-flex items-center gap-1 p-1.5 rounded-lg bg-rose-50 text-rose-700 hover:bg-rose-100" title="تنزيل PDF" data-testid={`pdf-survey-${s.id}`}>
                            <Download className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => deleteSurvey(s.id, s.student_name)} className="inline-flex items-center gap-1 p-1.5 rounded-lg bg-red-50 text-red-700 hover:bg-red-100" title="حذف الاستبيان" data-testid={`delete-survey-${s.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {selectedId && (
        <SurveyDetailModal detail={detail} onClose={() => { setSelectedId(null); setDetail(null); }} onDownloadPdf={() => downloadOnePdf(selectedId)} />
      )}
    </DashboardLayout>
  );
}

function SurveyDetailModal({ detail, onClose, onDownloadPdf }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl border border-slate-200 max-h-[92vh] flex flex-col" data-testid="survey-detail-modal">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="font-heading font-bold text-slate-900 text-lg">{detail?.student_name || "استبيان الطالب"}</h3>
            <div className="text-xs text-slate-500" dir="ltr">{detail?.student_email}</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onDownloadPdf} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rose-700 text-white text-xs font-semibold hover:bg-rose-600" data-testid="detail-pdf">
              <Download className="h-3.5 w-3.5" /> PDF
            </button>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="h-5 w-5" /></button>
          </div>
        </div>
        <div className="p-4 overflow-y-auto space-y-4">
          {!detail ? (
            <div className="grid place-items-center py-8"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
          ) : (
            Object.entries(detail.labels || {}).map(([k, label]) => {
              const v = detail.answers[k];
              if (v === undefined || v === null || v === "") return null;
              const shown = Array.isArray(v) ? v.join("، ") : typeof v === "boolean" ? (v ? "نعم" : "لا") : v;
              const isSkill = k.startsWith("skill_");
              return (
                <div key={k} className="flex items-start justify-between gap-4 py-2 border-b border-cream-100 last:border-0">
                  <div className="text-sm text-slate-600">{label}</div>
                  <div className={`text-sm font-semibold ${isSkill ? "arabic-numerals" : ""} text-slate-900 text-end`}>{shown}</div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
